import { btn, h, icon } from '../ui/icons';
import type { EngraveSession } from './EngraveSession';
import { ew, faceSwitch } from './parts';
import { type TempleSide } from './state';
import { TempleCard, type TempleCardWords } from './TempleCard';

/**
 * A110Z — desktop band under the face while the Gravure tab is open: Intérieur / Extérieur
 * selector, « Cliquez une zone pour la modifier », and the two temple cards B1 · B2 in the same
 * glass as the rail and the column. Clicking a zone selects its slot (the panel focuses the field).
 *
 * A203 (David, 11 octobre : « je veux un système pour baisser le menu des branches qui peut couper la vue ») : a
 * « Baisser les branches » button at the end of the head row folds the two cards away, leaving the row alone just
 * above the toolbar, so the glasses are seen whole; « Afficher les branches » brings them back. Remembered here.
 */
const LOW_KEY = 'mt-eng-band-low';
function readLow(): boolean {
  try { return window.localStorage.getItem(LOW_KEY) === '1'; } catch { return false; }
}
function writeLow(low: boolean): void {
  try { if (low) window.localStorage.setItem(LOW_KEY, '1'); else window.localStorage.removeItem(LOW_KEY); } catch { /* storage unavailable */ }
}
export function templeWords(mobile: boolean): TempleCardWords {
  const w = ew();
  return {
    temple: side => w.temple(side),
    face: face => (face === 'int' ? w.inner : w.outer),
    ghost: mobile ? w.tapToEngrave : w.clickToEngrave,
    slot: slot => w.slots[slot],
    spin: w.spin,
  };
}

export class EngraveBand {
  readonly root = h('div', 'mt-eng-band');
  private readonly cards: TempleCard[];
  private readonly faces;
  private readonly unsubscribe: () => void;
  /** A203: « Baisser les branches » / « Afficher les branches ». */
  private readonly lower = btn('mt-eng-band-lower');
  private cardsEl!: HTMLElement;

  constructor(private readonly session: EngraveSession) {
    const w = ew();
    this.root.hidden = true;
    this.root.setAttribute('role', 'group');
    this.root.setAttribute('aria-label', w.yourTemples(session.frame.model));
    const head = h('div', 'mt-eng-band-head');
    this.faces = faceSwitch(session, 'mt-eng-band-faces');
    const hint = h('span', 'mt-eng-band-hint', w.bandHint);
    this.lower.append(icon('chevron'), h('span', 'mt-eng-band-lower-label'));
    this.lower.onclick = () => this.setLow(this.root.dataset.low !== '1');
    head.append(this.faces.root, hint, this.lower);
    const cards = h('div', 'mt-eng-band-cards');
    cards.id = `mt-eng-band-cards-${Math.random().toString(36).slice(2, 8)}`;
    this.lower.setAttribute('aria-controls', cards.id);
    this.cardsEl = cards;
    this.cards = (['l', 'r'] as TempleSide[]).map(side => {
      const card = new TempleCard({
        side,
        words: templeWords(false),
        onSelect: slot => session.setActive(slot),
        onPlace: (slot, place) => session.setPlace(slot, place, { clamp: true }),
      });
      const glass = h('div', 'mt-eng-card mt-eng-band-card');
      glass.append(card.root);
      cards.append(glass);
      return card;
    });
    this.root.append(head, cards);
    this.setLow(readLow(), false);
    this.unsubscribe = session.onChange(() => this.refresh());
    this.refresh();
  }

  /** A203: folds the cards away (`low`) or shows them; the head row stays. */
  setLow(low: boolean, remember = true): void {
    const w = ew();
    this.root.dataset.low = low ? '1' : '';
    this.cardsEl.hidden = low;
    this.lower.setAttribute('aria-expanded', String(!low));
    const label = low ? w.bandShow : w.bandLower;
    this.lower.querySelector('.mt-eng-band-lower-label')!.textContent = label;
    this.lower.setAttribute('aria-label', label);
    if (remember) writeLow(low);
    if (!low) this.refresh();
  }

  get low(): boolean {
    return this.root.dataset.low === '1';
  }

  setVisible(visible: boolean): void {
    this.root.hidden = !visible;
    if (visible) this.refresh();
  }

  refresh(): void {
    if (this.root.hidden) return;
    const s = this.session;
    this.root.setAttribute('aria-label', ew().yourTemples(s.frame.model));
    this.faces.refresh();
    for (const card of this.cards) card.update({ templeId: s.frame.id, face: s.face, image: s.image, state: s.state, active: s.active });
  }

  destroy(): void {
    this.unsubscribe();
  }
}
